import { useCallback, useEffect, useState } from "react";
import { SYSTEMS } from "../integration";
import { IconAlert, IconClock, IconMail, IconClose } from "../icons";

/** Aviso publicado pelo Admin Cybersecurity no Portal (falha, manutenção ou comunicado). */
interface Aviso {
  id: number;
  tipo: "falha" | "manutencao" | "comunicado";
  titulo: string;
  mensagem: string;
  sistema_alvo: string | null;
  criado_em: string;
  expira_em: string | null;
}

const DISMISSED_KEY = "sercamp-avisos-dispensados";
const POLL_MS = 60_000;

function getDismissed(): number[] {
  try {
    const arr = JSON.parse(localStorage.getItem(DISMISSED_KEY) ?? "[]");
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}

function addDismissed(id: number) {
  const atual = getDismissed();
  if (atual.includes(id)) return;
  try {
    localStorage.setItem(DISMISSED_KEY, JSON.stringify([...atual, id]));
  } catch {
    /* localStorage indisponível (modo privado etc.) — dispensa só nesta sessão */
  }
}

const TIPO_INFO: Record<Aviso["tipo"], { icon: typeof IconAlert; cls: string }> = {
  falha: { icon: IconAlert, cls: "aviso-falha" },
  manutencao: { icon: IconClock, cls: "aviso-manutencao" },
  comunicado: { icon: IconMail, cls: "aviso-comunicado" },
};

/**
 * Faixa de avisos publicados pelo Portal (falhas, manutenções, comunicados).
 * Lida por polling num endpoint público e somente-leitura — sem autenticação,
 * sem nada enviado de volta ao Portal. Dispensar um aviso grava o id no
 * localStorage para não reaparecer nesta máquina/navegador.
 */
export default function AvisosBanner() {
  const [avisos, setAvisos] = useState<Aviso[]>([]);

  const load = useCallback(async () => {
    try {
      const res = await fetch(
        `${SYSTEMS.portal}/api/security/notificacoes/ativas?sistema=almoxarifado`,
      );
      if (!res.ok) return;
      const data = await res.json();
      if (!Array.isArray(data)) return;
      const agora = Date.now();
      const dispensados = getDismissed();
      const ativos = data.filter((a: Aviso) => {
        if (dispensados.includes(a.id)) return false;
        if (a.expira_em && new Date(a.expira_em).getTime() < agora) return false;
        return true;
      });
      setAvisos(ativos);
    } catch {
      /* aviso é secundário — falha silenciosa (Portal fora do ar, offline, etc.) */
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, POLL_MS);
    return () => clearInterval(id);
  }, [load]);

  function dispensar(id: number) {
    addDismissed(id);
    setAvisos((prev) => prev.filter((a) => a.id !== id));
  }

  if (avisos.length === 0) return null;

  return (
    <>
      {avisos.map((a) => {
        const info = TIPO_INFO[a.tipo] ?? TIPO_INFO.comunicado;
        const Icon = info.icon;
        return (
          <div key={a.id} className={`aviso-banner ${info.cls}`} role="alert">
            <span className="aviso-banner-ic">
              <Icon size={20} />
            </span>
            <div className="aviso-banner-txt">
              <strong>{a.titulo}</strong>
              <span>{a.mensagem}</span>
            </div>
            <button
              className="aviso-banner-close"
              onClick={() => dispensar(a.id)}
              title="Dispensar aviso"
              aria-label="Dispensar aviso"
            >
              <IconClose size={16} />
            </button>
          </div>
        );
      })}
    </>
  );
}
